import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { LogOut, ArrowLeft } from "lucide-react";
import Dashboard from "@/components/admin/Dashboard";
import Users from "@/components/admin/Users";
import Contents from "@/components/admin/Contents";
import Sos from "@/components/admin/Sos";
import Boutique from "@/components/admin/Boutique";
import Moteur from "@/components/admin/Moteur";
import Echanges from "@/components/admin/Echanges";
import Rdv from "@/components/admin/Rdv";
import Directory from "@/components/admin/Directory";
import Notifs from "@/components/admin/Notifs";
import Settings from "@/components/admin/Settings";
import MediaLibrary from "@/components/admin/MediaLibrary";
import Newsletter from "@/components/admin/Newsletter";
import Offers from "@/components/admin/Offers";
import Pillars from "@/components/admin/Pillars";
import Visio from "@/components/admin/Visio";
import Journal from "@/components/admin/Journal";
import Stats from "@/components/admin/Stats";
import Cms from "@/components/admin/Cms";
import Rgpd from "@/components/admin/Rgpd";
import Logs from "@/components/admin/Logs";
import PatientelePage from "@/components/admin/PatientelePage";
import Accompagnements from "@/components/admin/Accompagnements";
import LegalTexts from "@/components/admin/LegalTexts";
import Partners from "@/components/admin/Partners";
import Analytics from "@/components/admin/Analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU = [
  { id: "dashboard", label: "01 — Tableau de bord" },
  { id: "users", label: "02 — Patientèle" },
  { id: "moteur", label: "03 — Moteur Mon Instant" },
  { id: "ressentis", label: "04 — Ressentis & besoins" },
  { id: "contenus", label: "05 — Contenus" },
  { id: "piliers", label: "06 — Piliers" },
  { id: "sos", label: "07 — SOS 5 minutes" },
  { id: "echanges", label: "08 — Échanges" },
  { id: "rdv", label: "09 — Rendez-vous" },
  { id: "visio", label: "10 — Visio" },
  { id: "intervenants", label: "11 — Intervenants" },
  { id: "legal", label: "12 — Textes légaux" },
  { id: "journal", label: "12 bis — Journal" },
  { id: "notifications", label: "13 — Notifications" },
  { id: "newsletter", label: "14 — Communication" },
  { id: "offres", label: "15 — Abonnements & offres" },
  { id: "stats", label: "16 — Statistiques détaillées" },
  { id: "cms", label: "17 — CMS" },
  { id: "mediatheque", label: "18 — Médiathèque" },
  { id: "boutique", label: "19 — Boutique" },
  { id: "rgpd", label: "20 — RGPD" },
  { id: "logs", label: "21 — Journaux & sécurité" },
  { id: "parametres", label: "22 — Réglages" },
];
// « Mon accompagnement » n'apparaît dans ce menu que si Julie l'a activé
// (Réglages → « Activer le module Mon accompagnement »)
const ACCOMP_ITEM = { id: "accompagnement", label: "23 — Mon accompagnement" };
const PARTNERS_ITEM = { id: "partenaires", label: "24 — Partenaires & apporteurs" };
const ANALYTICS_ITEM = { id: "analytique", label: "25 — Analytique" };

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const isFichePage = location.pathname.startsWith("/admin/patientele/");
  const [user, setUser] = useState(null);
  const [module, setModuleState] = useState(() => localStorage.getItem("rav-admin-module") || "dashboard");
  // F5 : on rouvre la dernière rubrique visitée au lieu de retomber sur le tableau de bord
  const setModule = (m) => { localStorage.setItem("rav-admin-module", m); setModuleState(m); };
  const [accompEnabled, setAccompEnabled] = useState(false);
  const token = localStorage.getItem("rav-token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate("/profil");
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers })
      .then(({ data }) => {
        if (data.user.role !== "superadmin") navigate("/profil");
        else {
          setUser(data.user);
          // Le module Mon accompagnement n'apparaît que si activé dans Réglages
          axios.get(`${API}/settings/public`).then(({ data: s }) => setAccompEnabled(!!s.settings?.accompaniment_enabled)).catch(() => {});
        }
      })
      .catch(() => {
        localStorage.removeItem("rav-token");
        navigate("/profil");
      });
  }, [navigate]);

  const menu = [...(accompEnabled ? [...MENU, ACCOMP_ITEM] : MENU), PARTNERS_ITEM, ANALYTICS_ITEM];

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers });
    } catch {
      /* ignore */
    }
    localStorage.removeItem("rav-token");
    localStorage.removeItem("rav-profile");
    navigate("/");
  };

  if (!user) return null;

  return (
    <div className="rav-admin-layout" data-testid="admin-dashboard">
      <aside className="rav-admin-side">
        <img src="/logo-accueil.webp" alt="RAV" className="rav-admin-side-logo" />
        <p className="rav-admin-side-title">Super Administrateur</p>
        <p className="rav-admin-side-email" data-testid="admin-email">{user.email}</p>
        <img src="/divider-heart.webp" alt="" aria-hidden="true" className="rav-admin-side-divider" />
        <nav className="rav-admin-nav" data-testid="admin-nav">
          {menu.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`rav-admin-nav-item${module === m.id ? " is-active" : ""}`}
              data-testid={`admin-nav-${m.id}`}
              onClick={() => setModule(m.id)}
            >
              {m.label}
            </button>
          ))}
        </nav>
        <button type="button" className="rav-secondary-button rav-admin-logout rav-admin-public" data-testid="admin-back-public" onClick={() => navigate("/")}>
          <ArrowLeft size={14} strokeWidth={1.6} /> Retour dans mon espace Public
        </button>
        <button type="button" className="rav-secondary-button rav-admin-logout" data-testid="admin-logout-button" onClick={logout}>
          <LogOut size={14} strokeWidth={1.6} /> Se déconnecter
        </button>
      </aside>

      <main className="rav-admin-main">
        {isFichePage ? (
          <PatientelePage />
        ) : (
          <>
        {module === "dashboard" && <Dashboard goTo={setModule} />}
        {module === "users" && <Users />}
        {module === "moteur" && <Moteur />}
        {module === "ressentis" && (
          <Directory
            title="Ressentis & besoins"
            endpoint="feelings"
            testIdPrefix="feelings"
            fields={[
              { key: "name", label: "Nom (ex. Stressée)" },
              { key: "category", label: "Catégorie (Émotion, Énergie, Corps, Sommeil, Mental)" },
              { key: "pillars", label: "Piliers associés, séparés par des virgules (ex. respirer, ressentir)", type: "array" },
              { key: "needs", label: "Besoins proposés à l'étape 2, séparés par des virgules (4 max)", type: "array" },
            ]}
            columns={["name", "category", "pillars", "needs"]}
          />
        )}
        {module === "contenus" && <Contents />}
        {module === "sos" && <Sos />}
        {module === "echanges" && <Echanges />}
        {module === "rdv" && <Rdv />}
        {module === "legal" && <LegalTexts />}
        {module === "intervenants" && (
          <Directory
            title="Intervenants Respire Aime Vibre"
            endpoint="intervenants"
            testIdPrefix="intervenants"
            fields={[
              { key: "photo", label: "Photo", type: "photo" },
              { key: "name", label: "Prénom" },
              { key: "title", label: "Profession / spécialité (ex. Sophrologue)" },
              { key: "presentation", label: "Petit texte de présentation" },
              { key: "skills", label: "Compétences" },
              { key: "availability", label: "Disponibilités (ex. Mardi 10h00–17h00)" },
            ]}
            columns={["name", "title", "availability"]}
          />
        )}
        {module === "notifications" && <Notifs />}
        {module === "offres" && <Offers />}
        {module === "newsletter" && <Newsletter />}
        {module === "mediatheque" && <MediaLibrary />}
        {module === "boutique" && <Boutique />}
        {module === "piliers" && <Pillars />}
        {module === "visio" && <Visio />}
        {module === "journal" && <Journal />}
        {module === "stats" && <Stats />}
        {module === "cms" && <Cms />}
        {module === "rgpd" && <Rgpd />}
        {module === "logs" && <Logs />}
        {module === "parametres" && <Settings />}
        {module === "accompagnement" && accompEnabled && <Accompagnements />}
        {module === "partenaires" && <Partners />}
        {module === "analytique" && <Analytics />}
          </>
        )}
      </main>
    </div>
  );
}
